import { Loader } from 'components/common/Loader';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetObservationsMutation,
  IObservations,
  useGetCountsMutation,
  useGetLocationsMutation,
  useGetReportMutation,
} from 'redux/services/observation';
import moment from 'moment';
import { WorkFlowCard } from 'components/common/WorkFlowCard';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { Button, Pagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WorkFlowHeader } from 'components/common/WorkFlowHeader';
import { WORK_FLOW_OPTIONS } from 'pages/constants';
import {
  getAllSubTasks,
  getFilterValueByType,
  getSupportedFilterOptionForAPI,
} from 'pages/utils';
import { StartObservationModal } from './StartObservationModal';
import { ArrowBack } from '@mui/icons-material';
import toast from 'react-hot-toast';
import './Observation.scss';
import { downloadObservationReport } from 'pages/ReviewObservation/downloadObservationReport';

const Observation = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [selectedObservation, setSelectedObservation] =
    useState<IObservations | null>(null);
  const [getObservations, { data: response, isLoading: isObservationLoading }] =
    useGetObservationsMutation();
  const { project, isLoading } = useSetDropdownAndRole();
  const [getCounts, { data: counts, isLoading: countsLoading }] =
    useGetCountsMutation();
  const [getLocations, { data: locations }] = useGetLocationsMutation();
  const [status, setStatus] = useState('');
  const [filterValue, setFilterValue] = useState<{
    id?: string;
    name: string;
  } | null>(null);
  const [filterByValue, setFilterByValue] = useState<string | null>(null);
  const [filterByValues, setFilterByValues] = useState<
    { id?: string; name: string }[]
  >([]);
  const navigate = useNavigate();
  const [getReport] = useGetReportMutation();

  useEffect(() => {
    getObservations({ id: projectId });
  }, [getObservations, projectId]);

  useEffect(() => {
    getCounts({ id: projectId });
    getLocations({ id: projectId });
  }, [getCounts, getLocations, projectId]);

  useEffect(() => {
    if (selectedObservation) {
      setTimeout(() => {
        document.getElementById('download-btn')?.click();
      }, 500);
    }
  }, [selectedObservation]);

  const resetState = () => {
    setFilterByValues([]);
    setFilterByValue(null);
    setFilterValue(null);
  };

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (response?.pagination?.page !== value) {
      getObservations({
        id: projectId,
        page: value,
        status: status.toUpperCase(),
        filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
        filterValue: getFilterValueByType(filterValue, filterByValue),
      });
    }
  };

  const handleStatusChange = (changedStatus: string) => {
    setStatus(changedStatus.toUpperCase());
    getObservations({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: changedStatus.toUpperCase(),
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleClearStatus = () => {
    setStatus('');
    getObservations({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: '',
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleSelectOption = (option: string) => {
    if (filterValue) {
      setFilterValue(null);
      getObservations({
        id: projectId,
        page: Number(response?.pagination?.page ?? 1),
      });
    }
    setFilterByValue(option);
    if (option === 'User') {
      setFilterByValues(project?.team ?? []);
    } else if (option === 'Tags') {
      setFilterByValues(locations?.map((name: string) => ({ name })) ?? []);
    } else if (option === 'Sub Project') {
      setFilterByValues(getAllSubTasks(project?.tasks ?? []));
    } else {
      resetState();
    }
  };

  const handleSearchSelect = (option: { id?: string; name: string } | null) => {
    const filterValue = getFilterValueByType(option, filterByValue);

    setFilterValue(option);
    getObservations({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status,
      filterBy: filterValue
        ? getSupportedFilterOptionForAPI(filterByValue || '')
        : '',
      filterValue: filterValue || '',
    });
  };

  const handleClearSearchOptions = () => {
    resetState();
  };

  const getCardClickHandler = (observation: IObservations) => () => {
    setSelectedObservation(observation);
  };

  const getContinueDraftHandler = (item: IObservations) => () => {
    navigate(`/project/${projectId}/log-observation/${item._id}/1`);
  };

  const handleCloseStartObservationModal = () => {
    setSelectedObservation(null);
  };

  const handleBack = () => navigate(`/project/${projectId}`);

  const getDownloadHandler = (observationId: string) => async () => {
    toast.success('Report will be downloaded shortly');
    const res: any = await getReport({
      id: observationId,
    });

    if (res.data as IObservations) {
      downloadObservationReport(res.data, project?.team ?? []);
    } else {
      toast.error('Report generation failed. Please try again later');
    }
  };

  return (
    <div className="observation">
      <h4 className="page-title">{t('header_observation')}</h4>
      {response?.data?.length || status || filterByValue ? (
        <WorkFlowHeader
          open={counts?.OPEN}
          closed={counts?.CLOSE}
          draft={counts?.DRAFT}
          options={WORK_FLOW_OPTIONS}
          selectedSearchOption={filterValue}
          selectedOption={filterByValue}
          searchOptions={filterByValues}
          status={status}
          isLoading={countsLoading}
          onStatusClick={handleStatusChange}
          onOptionClick={handleSelectOption}
          onSearchSelect={handleSearchSelect}
          onClearStatus={handleClearStatus}
          onClearSearchOptions={handleClearSearchOptions}
        />
      ) : null}

      <div className="setup-container gap-3">
        {(response?.data as IObservations[])?.map((observation) => (
          <WorkFlowCard
            key={observation._id}
            title={observation.displayId ?? ''}
            role={project?.role ?? ''}
            date={moment(
              observation.updated_at || observation.created_at,
            ).format('DD/MM/YYYY')}
            status={observation.status || WorkFlowStatus.OPEN}
            doer={
              project?.team?.find(
                (teamObj) => teamObj.userId === observation.doer,
              )?.name ?? ''
            }
            inspector={
              project?.team?.find(
                (teamObj) => teamObj.userId === observation.inspector,
              )?.name ?? ''
            }
            areaOfObservation={observation.observationInfo?.areaOfObservations}
            projects={Object.values({
              subProject: observation.subProject?.name ?? '',
              subTask: observation.subTask?.name ?? '',
              endTask: observation.endTask?.name ?? '',
            }).filter((value) => value)}
            onDownload={getDownloadHandler(observation._id)}
            onCardClick={getCardClickHandler(observation)}
            onContinueDraft={getContinueDraftHandler(observation)}
          />
        ))}
        {!isObservationLoading &&
        !response?.data?.length &&
        !status &&
        !filterByValue ? (
          <div className="d-flex justify-content-center w-100 flex-grow-1 align-items-center flex-column">
            <div className="no-observation-placeholder" />
            <span className="h6 text-center">{t('no_observation_text')}</span>
          </div>
        ) : (
          !isObservationLoading &&
          !response?.data?.length &&
          (status || filterByValue) && (
            <span className="d-flex justify-content-center align-items-center w-100 h5">
              {t('no_matching_results')}
            </span>
          )
        )}
      </div>
      <div className="d-flex justify-content-between my-1">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          {t('btn_back')}
        </Button>
        {response?.pagination?.totalPage > 1 && (
          <Pagination
            className="align-self-end my-1"
            count={response?.pagination?.totalPage ?? 0}
            page={Number(response?.pagination?.page ?? 1)}
            color="primary"
            shape="rounded"
            onChange={handlePaginationChange}
          />
        )}
      </div>

      {(isLoading || isObservationLoading) && <Loader />}
      {selectedObservation && (
        <StartObservationModal
          open
          observation={selectedObservation}
          onClose={handleCloseStartObservationModal}
        />
      )}
    </div>
  );
};

export default Observation;
